import { lazy } from 'react';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useNavigate,
} from 'react-router-dom';
import { ConfigProvider } from 'antd';
import enUS from 'antd/locale/en_US';
import '../locales/i18n';
import '../style.css';
import useAuth from './hooks/useAuth';
import LazyWrapper from './utils/LazyWrapper';
import CrewDashboard from '../vyn/modules/Crew/CrewDashboard'
import UnAssignDashboard from '../vyn/modules/Crew/UnAssignDashboard'

import GlobalContextLayout from './layouts/GlobalContextLayout';
import Login from './routes/Login';
const OrganisationList = lazy(() => import('./routes/OrganisationList'));
const OrganisationOnboard = lazy(() => import('./routes/OrganisationOnboard'));
const EmployeeList = lazy(() => import('./routes/EmployeeList'));
const EmployeeOnboard = lazy(() => import('./routes/EmployeeOnboard'));
const ContractorOnboard = lazy(() => import('./routes/ContractorOnboard'));
const HomeLayout = lazy(() => import('./layouts/HomeLayout'));
const ProtectedRoute = lazy(() => import('./layouts/ProtectedRoute'));
const Approver1 = lazy(() => import('./routes/Approver1'));
const WorkmenDetails = lazy(() => import('./routes/WorkmenDetails'));
const EmployeeEdit = lazy(() => import('./routes/EmployeeEdit'));
const OrganisationEdit = lazy(() => import('./routes/OrganisationEdit'));
const Pfreport = lazy(() => import('./compliance/Pfreport'));
const Esireport = lazy(() => import('./compliance/Esireport'));
const Quickonboarding = lazy(() => import('./quickonboarding/quickonboarding'));
const Togglequickonboarding = lazy(() => import('./Setting/Togglequickonboarding'));
const Webcamera = lazy(() => import('./routes/Webcamera'));
const Dashboard = lazy(() => import('./compliance/Dashboard'));
const PermanentEmployeeList = lazy(() => import('./routes/PermanentEmployeeList'));
const Reportsafety = lazy(() => import('./components/IncidentReport/Reportsafety'));
const Ermanage = lazy(() => import('./components/IncidentReport/Ermanage'));
const Safetymanage = lazy(() => import('./components/IncidentReport/Safetymanage'));
const ChangePasswordPage = lazy(() => import('./views/AuthPage/changePassword'));
const ResetPasswordPage = lazy(() => import('./views/AuthPage/resetPassword'));
const Permission = lazy(() => import('./permission/permission'));
const Contractor = lazy(() => import('./routes/Contractor'));
const DesciplinaryActions = lazy(() => import('./historicalReview/DesciplinaryActions'));
const EmployeeGrowth = lazy(() => import('./historicalReview/EmployeeGrowth'));
const EmployeeMaster = lazy(() => import('./historicalReview/EmployeeMaster'));
const LongService = lazy(() => import('./historicalReview/LongService'));
const YearlyLeaves = lazy(() => import('./historicalReview/YearlyLeaves'));
const YearlyPoints = lazy(() => import('./historicalReview/YearlyPoints'));
const PermanentEmployeeOnboard = lazy(() => import('./routes/PermanentEmployeeOnboard'));
const PermanentEmployeeEdit = lazy(() => import('./routes/PermanentEmployeeEdit'));
const BulkUpload = lazy(() => import('./components/PermanentEmployeeOnboard/BulkUpload'));
const Audit = lazy(() => import('./audit/audit'));
const LicenceManagement = lazy(() => import('./LicenceManagement'));
const ContractorReport = lazy(() => import('./components/Reports/ContractorReport'));
const Regular = lazy(() => import('./components/Reports/Regular'));
const ContractorSummary = lazy(() => import('./components/ContractorOnboard/ContractorSummary'));
const ContractorApprovalPage = lazy(() => import('./routes/ContractorApprovalPage'));
const SamlResponse = lazy(() => import('./routes/SamlResponse'));
const IrDesignationForm = lazy(() => import('./components/PermanentEmployeeOnboard/IrDesignationForm'));
const ContractorSupervisorForm = lazy(() => import('./components/Contractor/ContractorSupervisorForm'));
const MedicalApproverStepper = lazy(() => import('./routes/MedicalApproverStepper'));
const SafetyApproverStepper = lazy(() => import('./routes/SafetyApproverStepper'));
const DigitalReport = lazy(() => import('./components/DigitalReport/digitalReport'));
const ApproverBulkDocumentUpload = lazy(() => import('./routes/ApproverBulkDocumentUpload'));
const MongoReportsDashboard = lazy(() => import('./modules/ReportsAndDashboard/Dashboard'));
const MongoReports = lazy(() => import('./modules/ReportsAndDashboard/Reports'));
const ManageReports = lazy(() => import('./modules/ReportsAndDashboard/ManageReports'));
const ScheduledCardsGrid = lazy(() => import('./modules/Tracker/Menu'));
const EmployeeSchedule = lazy(() => import('./modules/Tracker/TimeEntry'));
const EmployeePunchOut = lazy(() => import('./modules/Tracker/PunchOut'))
//CWFM
const MinimumWage = lazy(() => import('./cwfm/MininmumWage'));
const LrListWorkorderDetail = lazy(() => import('./cwfm/LrListWorkorderDetail'));
const WorkmenScheduler = lazy(() => import('./cwfm/WorkmenScheduler'));
const WorkOrderList = lazy(() => import('./cwfm/SectionHead/WorkOrderList'));
const WorkOrderDetail = lazy(() => import('./cwfm/SectionHead/WorkOrderDetail'));
const LaborRequisitionDetail = lazy(() => import('./cwfm/SectionHead/LaborRequisitionDetail'));
const LaborRequisitionList = lazy(() => import('./cwfm/DepartmentHead/LaborRequisitionList'));
const LaborRequisitionDetails = lazy(() => import('./cwfm/DepartmentHead/LaborRequisitionDetails'));
const FormBuilder = lazy(() => import('./components/CustomForm/FormBuilder'));
const FormLists = lazy(() => import('./components/CustomForm/FormLists'));
const FormRender = lazy(() => import('./components/CustomForm/FormLists'));
const FormEdit = lazy(() => import('./components/CustomForm/FormEdit'));
const WageRegister = lazy(() => import("./cwfm/WageRegister"));
const VendorScoring = lazy(() => import('./modules/VendorScoring'));
const VendorPerformanceConfiguration = lazy(() => import('./modules/VendorScoring/Configuration'));
const DynamicApproverTable = lazy(() => import('./components/Contractor/OrganisationLocationDynamicApprover'));
const DynamicEmployeeOnboard = lazy(() => import('./routes/DynamicEmployeeOnboard'));
const EventsTable = lazy(() => import('./modules/SelfOnboarding/Events'));
const WorkmenListByEventId = lazy(() => import('./modules/SelfOnboarding/Events/WorkmenList'));
const ProcessWorkmanAuthLink = lazy(() => import('./modules/SelfOnboarding/Auth/ProcessWorkmanAuthLink'));
const VynInsights = lazy(() => import('./components/VynChatBot/vynInsights'));
const WorkmenListForI9Submissions = lazy(() => import('./modules/SelfOnboarding/I9Submissions/WorkmenList'));
const I9SubmissionDetails = lazy(() => import('./modules/SelfOnboarding/I9Submissions/WorkmanDetails'));

const RedirectToNav = () => {
  const { user, userConfig, tokenInfo } = useAuth();
  const navigate = useNavigate();

  if (!user.isAuthenticated) {
    navigate('/login');
  }

  return <Navigate to={userConfig.nav} />;
};

const router = createBrowserRouter([
  {
    path: '/saml/response/:token?',
    element: <SamlResponse />,
  },

  {
    element: <GlobalContextLayout />,
    children: [
      {
        path: '/',
        element: <LazyWrapper component={ProtectedRoute} />,
        children: [
          // {
          //   path: '',
          //   element: <RedirectToNav />,
          // },
          {
            path: 'home',
            element: <LazyWrapper component={HomeLayout} />,
            children: [
              //organization Onboard
              {
                path: 'sa',
                element: <LazyWrapper component={OrganisationList} />
              },
              {
                path: 'sa/onboard',
                element: <LazyWrapper component={OrganisationOnboard} />,
              },
              {
                path: 'sa/licence-management',
                element: <LazyWrapper component={LicenceManagement} />
              },
              {
                path: 'sa/org/:id',
                element: <LazyWrapper component={OrganisationEdit} />,
              },
              {
                path: 'sa/insights',
                element: <LazyWrapper component={VynInsights} />,
              },

              // Supervisor Onboard
              {
                path: 'admin',
                element: <LazyWrapper component={Contractor} />,
                // element: {ContractorComponent},
              },
              {
                path: 'change-password',
                element: <LazyWrapper component={ChangePasswordPage} />,
              },
              {
                path: 'admin/organisation',
                element: <LazyWrapper component={ContractorOnboard} />,
                // element: PrincipalEmployerComponent,
              },

              {
                path: 'admin/compliance/pf-report',
                element: <LazyWrapper component={Pfreport} />,
              },
              {
                path: 'admin/compliance/esi-report',
                element: <LazyWrapper component={Esireport} />,
              },
              {
                path: 'admin/compliance/dashboard',
                element: <LazyWrapper component={Dashboard} />,
              },
              {
                path: 'admin/setting',
                element: <LazyWrapper component={Togglequickonboarding} />,
              },
              {
                path: 'admin/audit',
                element: <LazyWrapper component={Audit} />,
              },
              {
                path: 'admin/audit',
                element: <LazyWrapper component={ContractorSummary} />,
              },
              {
                path: 'admin/permission',
                element: <LazyWrapper component={Permission} />,
              },
              {
                path: 'admin/insights',
                element: <LazyWrapper component={VynInsights} />,
              },
              //Employee Onboard
              {
                path: 'supervisor/employee',
                element: <LazyWrapper component={EmployeeList} />,
              },
              {
                path: 'supervisor',
                element: <LazyWrapper component={EmployeeOnboard} />,
              },
              {
                path: 'supervisor/crewManagement',
                element: <LazyWrapper component={CrewDashboard} />,
              },
              {
                path: 'admin/formbuilder',
                element: <LazyWrapper component={FormBuilder} />,
              },
              {
                path: '/home/admin/formlist',
                element: <LazyWrapper component={FormLists} />,
              },
              {
                path: '/home/supervisor/view/:formId/v/:version',
                element: <LazyWrapper component={FormRender} />,
              },
              {
                path: '/home/supervisor/edit/:formId/v/:version',
                element: <LazyWrapper component={FormEdit} />,
              },
              {
                path: 'supervisor/qeo',
                element: <LazyWrapper component={Quickonboarding} />,
              },
              {
                path: 'supervisor/bulk',
                element: <LazyWrapper component={Webcamera} />,
              },
              {
                path: 'supervisor/digitalreport',
                element: <LazyWrapper component={DigitalReport} />,
              },
              {
                path: 'supervisor/employee/:id',
                element: <LazyWrapper component={EmployeeEdit} />,
              },
              {
                path: 'supervisor/incidentreport',
                element: <LazyWrapper component={Reportsafety} />,
              },
              {
                path: 'supervisor/scheduleworkmen',
                element: <LazyWrapper component={LrListWorkorderDetail} />,
              },
              {
                path: 'supervisor/scheduleworkmen/:workOrderNumber',
                element: <LazyWrapper component={WorkmenScheduler} />,
              },
              {
                path: 'supervisor/tracker/menu',
                element: <LazyWrapper component={ScheduledCardsGrid} />,
              },
              {
                path: 'supervisor/tracker/time-entry',
                element: <LazyWrapper component={EmployeeSchedule} />,
              },
              {
                path: 'supervisor/tracker/punch_out',
                element: <LazyWrapper component={EmployeePunchOut} />
              },
              //Approver
              {
                path: 'approver/list',
                element: <LazyWrapper component={Approver1} />,
              },
              {
                path: 'approver/employee/:id',
                element: <LazyWrapper component={EmployeeEdit} />,
              },
              {
                path: 'approver/employee/:id/medical',
                element: <LazyWrapper component={MedicalApproverStepper} />,
              },
              {
                path: 'approver/employee/:id/safety',
                element: <LazyWrapper component={SafetyApproverStepper} />,
              },
              {
                path: 'workmenList/:id',
                element: <LazyWrapper component={EmployeeEdit} />,
              },

              //INCIDENT REPORT
              {
                path: 'approver/incidentreport',
                element: <LazyWrapper component={Reportsafety} />,
              },
              {
                path: 'approver/incidentreport/:id',
                element: <LazyWrapper component={Ermanage} />,
              },
              {
                path: 'supervisor/incidentreport/:id',
                element: <LazyWrapper component={Ermanage} />,
              },
              {
                path: 'approver/incidentreport/:id/safety',
                element: <LazyWrapper component={Safetymanage} />,
              },
              // {
              //   path: 'approver/contractorApproval',
              //   element: <Contractor />,
              // },
              {
                path: 'approver/contractorApproval',
                element: <LazyWrapper component={ContractorApprovalPage} />,
              },
              {
                path: 'approver/report/contractor',
                element: <LazyWrapper component={ContractorReport} />,
              },
              {
                path: 'approver/minimum-wage',
                element: <LazyWrapper component={MinimumWage} />,
              },
              {
                path: 'approver/wage-register',
                element: <LazyWrapper component={WageRegister} />,
              },
              {
                path: 'approver/insights',
                element: <LazyWrapper component={VynInsights} />,
              },

              //Historical Review
              {
                path: 'approver/historical/longService',
                element: <LazyWrapper component={LongService} />,
              },
              {
                path: 'approver/historical/employeeMaster',
                element: <LazyWrapper component={EmployeeMaster} />,
              },
              {
                path: 'approver/historical/yearlyPoints',
                element: <LazyWrapper component={YearlyPoints} />,
              },
              {
                path: 'approver/historical/desciplinaryActions',
                element: <LazyWrapper component={DesciplinaryActions} />,
              },
              {
                path: 'approver/historical/yearlyLeaves',
                element: <LazyWrapper component={YearlyLeaves} />,
              },
              {
                path: 'approver/historical/employeeGrowth',
                element: <LazyWrapper component={EmployeeGrowth} />,
              },
              {
                path: 'approver/bulk',
                element: <LazyWrapper component={ApproverBulkDocumentUpload} />,
              },
              //Department
              {
                path: 'department/sectionhead/workorderlist',
                element: <LazyWrapper component={WorkOrderList} />,
              },
              {
                path: 'department/sectionhead/workorderlist/workorderDetails/:workOrderNumber',
                element: <LazyWrapper component={WorkOrderDetail} />,
              },
              {
                path: 'department/sectionhead/workorderlist/laborRequisitionDetail/workOrder/:workOrderNumber',
                element: <LazyWrapper component={LaborRequisitionDetail} />,
              },
              {
                path: 'department/sectionhead/workorderlist/laborRequisitionDetail/lr/:lrNumber',
                element: <LazyWrapper component={LaborRequisitionDetail} />,
              },
              //Department
              {
                path: 'department/departmenthead/workorderlist',
                element: <LazyWrapper component={WorkOrderList} />,
              },
              {
                path: 'department/departmenthead/lrlist',
                element: <LazyWrapper component={LaborRequisitionList} />,
              },
              {
                path: 'department/departmenthead/lrlist/laborRequisitionList/lr/:lrNumber',
                element: <LazyWrapper component={LaborRequisitionDetails} />,
              },
              //shop
              {
                path: 'shop/historical/longService',
                element: <LazyWrapper component={LongService} />,
              },
              {
                path: 'shop/historical/employeeMaster',
                element: <LazyWrapper component={EmployeeMaster} />,
              },
              {
                path: 'shop/historical/yearlyPoints',
                element: <LazyWrapper component={YearlyPoints} />,
              },
              {
                path: 'shop/historical/desciplinaryActions',
                element: <LazyWrapper component={DesciplinaryActions} />,
              },
              {
                path: 'shop/historical/yearlyLeaves',
                element: <LazyWrapper component={YearlyLeaves} />,
              },
              {
                path: 'shop/historical/employeeGrowth',
                element: <LazyWrapper component={EmployeeGrowth} />,
              },
              //IR
              {
                path: 'ir/employee',
                element: <LazyWrapper component={PermanentEmployeeList} />,
              },

              {
                path: 'ir',
                element: <LazyWrapper component={PermanentEmployeeOnboard} />,
              },
              {
                path: 'ir/bulk',
                element: <LazyWrapper component={BulkUpload} />,
              },
              {
                path: 'ir/dashBoard',
                element: <LazyWrapper component={IrDesignationForm} />,
              },

              {
                path: 'ir/employee/:id',
                element: <LazyWrapper component={PermanentEmployeeEdit} />,
              },

              {
                path: 'ir/historical/longService',
                element: <LazyWrapper component={LongService} />,
              },
              {
                path: 'ir/historical/employeeMaster',
                element: <LazyWrapper component={EmployeeMaster} />,
              },
              {
                path: 'ir/historical/yearlyPoints',
                element: <LazyWrapper component={YearlyPoints} />,
              },
              {
                path: 'ir/historical/desciplinaryActions',
                element: <LazyWrapper component={DesciplinaryActions} />,
              },
              {
                path: 'ir/historical/yearlyLeaves',
                element: <LazyWrapper component={YearlyLeaves} />,
              },
              {
                path: 'ir/historical/employeeGrowth',
                element: <LazyWrapper component={EmployeeGrowth} />,
              },
              {
                path: 'ir/report/regular',
                element: <LazyWrapper component={Regular} />,
              },
              {
                path: 'ir/insights',
                element: <LazyWrapper component={VynInsights} />,
              },

              //contractor
              {
                path: 'contractor',
                element: <LazyWrapper component={Contractor} />,
              },
              {
                path: 'managesupervisor',
                element: <LazyWrapper component={ContractorSupervisorForm} />,
              },
              {
                path: 'workmenDetails',
                element: <LazyWrapper component={WorkmenDetails} />,
              },
              {
                path: 'reports/dashboard',
                element: <LazyWrapper component={MongoReportsDashboard} />,
              },
              {
                path: 'reports',
                element: <LazyWrapper component={MongoReports} />,
              },
              {
                path: 'reports/manage',
                element: <LazyWrapper component={ManageReports} />,
              },
              {
                path: 'vendor-scoring/dashboard',
                element: <LazyWrapper component={VendorScoring} />,
              },
              {
                path: 'vendor-scoring/configure',
                element: <LazyWrapper component={VendorPerformanceConfiguration} />,
              },
              {
                path: 'sa/dynamicForm/dynamicApprover',
                element: <LazyWrapper component={DynamicApproverTable} />,
              },
              {
                path: 'supervisor',
                element: <LazyWrapper component={DynamicEmployeeOnboard} />,
              },

              {
                path: 'sa/dynamicForm/formbuilder',
                element: <LazyWrapper component={FormBuilder} />,
              },
              {
                path: 'sa/dynamicForm/formlist',
                element: <LazyWrapper component={FormLists} />,
              },
              {
                path: '/home/supervisor/view/:formId/v/:version',
                element: <LazyWrapper component={FormRender} />,
              },
              {
                path: '/home/supervisor/edit/:formId/v/:version',
                element: <LazyWrapper component={FormEdit} />,
              },
              {
                path: '/home/events',
                element: <LazyWrapper component={EventsTable} />,
              },
              {
                path: '/home/events/:eventId',
                element: <LazyWrapper component={WorkmenListByEventId} />,
              },
              {
                path: '/home/i9-submissions',
                element: <LazyWrapper component={WorkmenListForI9Submissions} />,
              },
              {
                path: '/home/i9-submissions/:workmanId',
                element: <LazyWrapper component={I9SubmissionDetails} />,
              },
              {
                path: '/home/workman/:id',
                element: <LazyWrapper component={DynamicEmployeeOnboard} />,
              },
            ],
          },
        ],
      },
      {
        path: '/login',
        element: <Login />,
      },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      {
        path: '/auth/workman',
        element: <ProcessWorkmanAuthLink />,
      },
    ],
  },
]);

function App() {
  return (
    <ConfigProvider locale={enUS}>
      <RouterProvider router={router} />
    </ConfigProvider>
  );
}

export default App;



